import { Autocomplete as AutocompletePrimitive } from "@base-ui/react";
import searchIcon from "@tabler/icons/outline/search.svg";
import xIcon from "@tabler/icons/outline/x.svg";
import React, { useId, useMemo, useRef, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import PlaceholderDropdownMenuItem from "@/features/placeholder/components/placeholder-dropdown-menu-item";
import { cn } from "@/utils/cn";

import { Icon, Input, Stack, Text } from "../ui";

import { RecentSearchesHeader, RecentSearchRow } from "./recent-searches";

import type { InputVariant } from "../ui/input/input";

interface ComboboxProps<T> {
  label: string;
  value: string;
  onValueChange(value: string): void;

  /** Absent = the popup never shows a results list (query-only combobox). */
  results?: {
    data: T[];
    isLoading: boolean;
    isLoaded: boolean;
    /** Max rows to render; negative means no cap. Defaults to 5. */
    limit?: number;
    itemContent(item: T): React.ReactNode;
    itemTestId?(item: T): string;
    onSelect(item: T | null): void;
  };

  /** Shown in place of the results whenever the query is empty. */
  recentSearches?: {
    items: string[];
    onRemove(term: string): void;
    onClearAll(): void;
  };

  /** An optional trailing, keyboard-navigable row appended after the results. */
  footerItem?: {
    content(query: string): React.ReactNode;
    onSelect(): void;
    testId?: string;
  };

  autoFocus?: boolean;
  inputVariant?: InputVariant;
  placeholderText?: string;
  side?: "top" | "bottom";
  onFocus?(): void;
  onBlur?(): void;
  /** Enter pressed with no item highlighted. Defaults to `results.onSelect(null)`. */
  onEnterWithoutSelection?(rawValue: string): void;
}

type Row<T> =
  | { kind: "recent"; term: string }
  | { kind: "result"; item: T }
  | { kind: "footer" };

const messages = defineMessages({
  placeholder: { id: "combobox.placeholder", defaultMessage: "Search" },
  clearSearch: { id: "combobox.clear_search", defaultMessage: "Clear search" },
});

const NO_RECENT_SEARCHES: string[] = [];

const itemClassName =
  "block cursor-pointer px-4 py-2.5 text-sm text-gray-700 outline-hidden hover:bg-gray-100 data-highlighted:bg-gray-100 dark:text-gray-500 dark:hover:bg-gray-800 dark:data-highlighted:bg-gray-800";

export default function Combobox<T>({
  autoFocus,
  results,
  recentSearches,
  footerItem,
  inputVariant = "search",
  label,
  value,
  onValueChange,
  onFocus,
  onBlur,
  placeholderText,
  side = "bottom",
  onEnterWithoutSelection,
}: ComboboxProps<T>) {
  const intl = useIntl();

  const highlightedItemRef = useRef<unknown>(undefined);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const name = `combobox-${label}`;
  const id = `${name}-${useId()}`;
  const hasValue = !!value;

  function setValue(nextValue: string) {
    highlightedItemRef.current = undefined;
    onValueChange(nextValue);
  }

  function handleClear() {
    setValue("");
  }

  function handleEnterWithoutSelection() {
    if (onEnterWithoutSelection) {
      onEnterWithoutSelection(value);
    } else {
      results?.onSelect(null);
    }
    setIsOpen(false);
  }

  function selectRecentSearch(searchValue: string) {
    setValue(searchValue);
    setIsOpen(true);
  }

  const hasQuery = value.length > 0;
  const recentSearchItems = recentSearches?.items ?? NO_RECENT_SEARCHES;

  const limit = results?.limit ?? 5;
  // `limit` caps the results only. Recent searches are a separate list and
  // the caller decides how many of them to hand over.
  const resultsData = results?.data;
  const limitedResults = useMemo(
    () =>
      !resultsData ? [] : limit < 0 ? resultsData : resultsData.slice(0, limit),
    [resultsData, limit],
  );

  const shouldShowRecentSearches = !hasQuery && recentSearchItems.length > 0;
  const isSearching = !!results && hasQuery && results.isLoading;
  const hasSearched =
    !!results && hasQuery && !results.isLoading && results.isLoaded;
  const shouldShowNoResults =
    hasSearched && limitedResults.length === 0 && !footerItem;
  const shouldShowResultsList =
    hasSearched && (limitedResults.length > 0 || !!footerItem);

  const shouldShowPanel =
    isSearching ||
    shouldShowNoResults ||
    shouldShowResultsList ||
    shouldShowRecentSearches;

  const hasFooter = !!footerItem;
  const rows = useMemo<Row<T>[]>(() => {
    if (shouldShowRecentSearches) {
      return recentSearchItems.map((term) => ({ kind: "recent", term }));
    }
    if (!shouldShowResultsList) return [];

    const resultRows: Row<T>[] = limitedResults.map((item) => ({
      kind: "result",
      item,
    }));
    return hasFooter ? [...resultRows, { kind: "footer" }] : resultRows;
  }, [
    shouldShowRecentSearches,
    recentSearchItems,
    shouldShowResultsList,
    limitedResults,
    hasFooter,
  ]);

  return (
    <Stack className="w-full">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>

      <AutocompletePrimitive.Root
        items={rows}
        filter={null}
        value={value}
        onValueChange={(nextValue: string, eventDetails) => {
          // Clicking/selecting an item fills the input with its stringified
          // value by default; selection must never overwrite what was typed.
          if (eventDetails.reason === "item-press") {
            eventDetails.cancel();
            return;
          }

          setValue(nextValue);
          setIsOpen(!!nextValue || recentSearchItems.length > 0);
        }}
        onItemHighlighted={(item) => {
          highlightedItemRef.current = item;
        }}
        open={isOpen && shouldShowPanel}
        onOpenChange={(nextIsOpen, eventDetails) => {
          // Emptying the input closes the popup by default, but the recent
          // searches that take over the panel are exactly what belongs there.
          if (
            !nextIsOpen &&
            eventDetails.reason === "input-clear" &&
            recentSearchItems.length > 0
          ) {
            eventDetails.cancel();
            return;
          }

          if (!nextIsOpen) {
            highlightedItemRef.current = undefined;
          }

          setIsOpen(nextIsOpen);
        }}
      >
        <AutocompletePrimitive.Input
          render={
            <Input
              data-testid="combobox-search"
              id={id}
              variant={inputVariant}
              name={name}
              autoFocus={autoFocus}
              placeholder={
                placeholderText || intl.formatMessage(messages.placeholder)
              }
              outerClassName="mt-0 w-full"
              append={
                inputVariant === "search" ? (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="group"
                    data-testid="combobox-clear"
                    aria-label={intl.formatMessage(messages.clearSearch)}
                  >
                    <Icon
                      src={hasValue ? xIcon : searchIcon}
                      className={cn("size-5 text-gray-500 dark:text-gray-600", {
                        "group-hover:text-gray-900 dark:group-hover:text-gray-100":
                          hasValue,
                        "cursor-default": !hasValue,
                      })}
                      aria-hidden="true"
                    />
                  </button>
                ) : undefined
              }
            />
          }
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            if (highlightedItemRef.current === undefined) {
              handleEnterWithoutSelection();
            }
          }}
          onFocus={() => {
            onFocus?.();

            // Unconditional: `shouldShowPanel` already gates whether there is
            // anything worth showing.
            setIsOpen(true);
          }}
          onBlur={() => onBlur?.()}
        />

        <AutocompletePrimitive.Portal>
          <AutocompletePrimitive.Positioner
            side={side}
            sideOffset={side === "top" ? 8 : 1}
            align="start"
            className="isolate z-1003"
          >
            <AutocompletePrimitive.Popup
              className={cn(
                "w-(--anchor-width) rounded-md bg-white py-1 opacity-100 shadow-lg transition-[opacity,transform] duration-200 focus:outline-hidden data-ending-style:scale-[0.8] data-ending-style:opacity-0 data-starting-style:scale-[0.8] data-starting-style:opacity-0 dark:bg-gray-900 dark:ring-2 dark:ring-gray-800",
              )}
            >
              {isSearching && (
                <>
                  <PlaceholderDropdownMenuItem minLength={6} maxLength={14} />
                  <PlaceholderDropdownMenuItem minLength={6} maxLength={14} />
                </>
              )}

              {shouldShowNoResults && (
                <Text size="sm" theme="muted" className="p-4 text-center">
                  <FormattedMessage
                    id="combobox.blankslate.title"
                    defaultMessage='No results for "{searchValue}"'
                    values={{ searchValue: value }}
                  />
                </Text>
              )}

              {shouldShowRecentSearches && recentSearches && (
                <RecentSearchesHeader onClearAll={recentSearches.onClearAll} />
              )}

              <AutocompletePrimitive.List>
                {(row: Row<T>, index: number) => {
                  switch (row.kind) {
                    case "footer": {
                      if (!footerItem) return null;

                      return (
                        <AutocompletePrimitive.Item
                          key="combobox-footer"
                          value={row}
                          index={index}
                          className={itemClassName}
                          data-testid={
                            footerItem.testId ?? "combobox-footer-item"
                          }
                          onClick={() => footerItem.onSelect()}
                        >
                          {footerItem.content(value)}
                        </AutocompletePrimitive.Item>
                      );
                    }

                    case "recent": {
                      if (!recentSearches) return null;

                      return (
                        <AutocompletePrimitive.Item
                          key={row.term}
                          value={row}
                          index={index}
                          className={itemClassName}
                          data-testid="recent-search"
                          onClick={() => selectRecentSearch(row.term)}
                        >
                          <RecentSearchRow
                            term={row.term}
                            onRemove={() => recentSearches.onRemove(row.term)}
                          />
                        </AutocompletePrimitive.Item>
                      );
                    }

                    case "result": {
                      if (!results) return null;

                      return (
                        <AutocompletePrimitive.Item
                          key={index}
                          value={row}
                          index={index}
                          className={itemClassName}
                          data-testid={results.itemTestId?.(row.item)}
                          onClick={() => results.onSelect(row.item)}
                        >
                          {results.itemContent(row.item)}
                        </AutocompletePrimitive.Item>
                      );
                    }
                  }
                }}
              </AutocompletePrimitive.List>
            </AutocompletePrimitive.Popup>
          </AutocompletePrimitive.Positioner>
        </AutocompletePrimitive.Portal>
      </AutocompletePrimitive.Root>
    </Stack>
  );
}
